import type {
  ElementoBarras,
  ElementoBruto,
  ElementoCaixa,
  ElementoImagem,
  ElementoQrcode,
  ElementoTexto,
  EtiquetaDesign,
  EtiquetaElemento,
  FonteZpl,
  Rotacao,
  Simbologia,
} from "@/types/etiqueta-designer";

/* ------------------------------------------------------------------ */
/* Unidades                                                            */
/* ------------------------------------------------------------------ */

/** dots por mm da cabeça de impressão (mesma tabela usada no resto do site) */
export const dpmmDe = (dpi: string | number): number => {
  switch (String(dpi)) {
    case "300":
      return 12;
    case "600":
      return 24;
    case "203":
    default:
      return 8;
  }
};

export const mmParaDots = (mm: number, dpmm: number) =>
  Math.round((Number(mm) || 0) * dpmm);

export const dotsParaMm = (dots: number, dpmm: number) =>
  Math.round(((Number(dots) || 0) / dpmm) * 100) / 100;

const ROTACAO_ZPL: Record<Rotacao, string> = {
  0: "N",
  90: "R",
  180: "I",
  270: "B",
};

const ZPL_ROTACAO: Record<string, Rotacao> = {
  N: 0,
  R: 90,
  I: 180,
  B: 270,
};

export const novoId = () =>
  `el_${Math.random().toString(36).slice(2, 9)}${(performance.now() | 0).toString(36)}`;

/* ------------------------------------------------------------------ */
/* Bitmap monocromático / ^GFA                                         */
/* ------------------------------------------------------------------ */

export type BitmapMono = {
  largura: number;
  altura: number;
  bytesPorLinha: number;
  /** 1 bit por pixel, 1 = preto, empacotado MSB primeiro */
  dados: Uint8Array;
};

export const criarBitmap = (largura: number, altura: number): BitmapMono => {
  const bytesPorLinha = Math.ceil(largura / 8);
  return {
    largura,
    altura,
    bytesPorLinha,
    dados: new Uint8Array(bytesPorLinha * altura),
  };
};

export const lerPixel = (bmp: BitmapMono, x: number, y: number): boolean => {
  if (x < 0 || y < 0 || x >= bmp.largura || y >= bmp.altura) return false;
  const byte = bmp.dados[y * bmp.bytesPorLinha + (x >> 3)];
  return ((byte >> (7 - (x & 7))) & 1) === 1;
};

export const escreverPixel = (
  bmp: BitmapMono,
  x: number,
  y: number,
  preto: boolean,
) => {
  if (x < 0 || y < 0 || x >= bmp.largura || y >= bmp.altura) return;
  const i = y * bmp.bytesPorLinha + (x >> 3);
  const mask = 1 << (7 - (x & 7));
  if (preto) bmp.dados[i] |= mask;
  else bmp.dados[i] &= ~mask;
};

export const bitmapParaHex = (bmp: BitmapMono): string => {
  let out = "";
  for (let i = 0; i < bmp.dados.length; i++) {
    out += bmp.dados[i].toString(16).toUpperCase().padStart(2, "0");
  }
  return out;
};

export const elementoImagemDeBitmap = (
  bmp: BitmapMono,
): Pick<
  ElementoImagem,
  "larguraDots" | "alturaDots" | "bytesPorLinha" | "hex"
> => ({
  larguraDots: bmp.largura,
  alturaDots: bmp.altura,
  bytesPorLinha: bmp.bytesPorLinha,
  hex: bitmapParaHex(bmp),
});

export const bitmapDeElemento = (el: ElementoImagem): BitmapMono => {
  const bytesPorLinha = el.bytesPorLinha || Math.ceil(el.larguraDots / 8);
  const total = bytesPorLinha * el.alturaDots;
  const dados = new Uint8Array(total);
  const hex = el.hex || "";
  for (let i = 0; i < total; i++) {
    const par = hex.substr(i * 2, 2);
    dados[i] = par.length === 2 ? parseInt(par, 16) : 0;
  }
  return { largura: el.larguraDots, altura: el.alturaDots, bytesPorLinha, dados };
};

/** Rotaciona o bitmap em múltiplos de 90° (^GF não respeita ^FWR). */
export const rotacionarBitmap = (
  bmp: BitmapMono,
  graus: Rotacao,
): BitmapMono => {
  if (graus === 0) return bmp;
  const trocaEixos = graus === 90 || graus === 270;
  const destino = criarBitmap(
    trocaEixos ? bmp.altura : bmp.largura,
    trocaEixos ? bmp.largura : bmp.altura,
  );
  for (let y = 0; y < bmp.altura; y++) {
    for (let x = 0; x < bmp.largura; x++) {
      if (!lerPixel(bmp, x, y)) continue;
      if (graus === 90) escreverPixel(destino, bmp.altura - 1 - y, x, true);
      else if (graus === 180)
        escreverPixel(destino, bmp.largura - 1 - x, bmp.altura - 1 - y, true);
      else escreverPixel(destino, y, bmp.largura - 1 - x, true);
    }
  }
  return destino;
};

/** Reamostra por vizinho mais próximo (usado ao redimensionar sem o original). */
export const redimensionarBitmap = (
  bmp: BitmapMono,
  largura: number,
  altura: number,
): BitmapMono => {
  const destino = criarBitmap(Math.max(1, largura), Math.max(1, altura));
  for (let y = 0; y < destino.altura; y++) {
    const sy = Math.min(
      bmp.altura - 1,
      Math.floor((y * bmp.altura) / destino.altura),
    );
    for (let x = 0; x < destino.largura; x++) {
      const sx = Math.min(
        bmp.largura - 1,
        Math.floor((x * bmp.largura) / destino.largura),
      );
      if (lerPixel(bmp, sx, sy)) escreverPixel(destino, x, y, true);
    }
  }
  return destino;
};

export type OpcoesImagem = {
  larguraDots: number;
  alturaDots: number;
  /** 0-255; pixels mais escuros viram preto */
  limiar: number;
  /** difusão de erro Floyd-Steinberg em vez de limiar puro */
  dither: boolean;
  inverter: boolean;
};

/**
 * Converte um <img>/bitmap carregado em bitmap 1bpp no tamanho pedido.
 * Pixels transparentes são tratados como branco.
 */
export const imagemParaBitmap = (
  fonte: CanvasImageSource,
  opcoes: OpcoesImagem,
): BitmapMono => {
  const largura = Math.max(1, Math.round(opcoes.larguraDots));
  const altura = Math.max(1, Math.round(opcoes.alturaDots));

  const canvas = document.createElement("canvas");
  canvas.width = largura;
  canvas.height = altura;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return criarBitmap(largura, altura);

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, largura, altura);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(fonte, 0, 0, largura, altura);

  const { data } = ctx.getImageData(0, 0, largura, altura);
  // luminância em float pra permitir difusão de erro
  const cinza = new Float32Array(largura * altura);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    const a = data[i + 3] / 255;
    const r = data[i] * a + 255 * (1 - a);
    const g = data[i + 1] * a + 255 * (1 - a);
    const b = data[i + 2] * a + 255 * (1 - a);
    let lum = 0.299 * r + 0.587 * g + 0.114 * b;
    if (opcoes.inverter) lum = 255 - lum;
    cinza[p] = lum;
  }

  const bmp = criarBitmap(largura, altura);
  const limiar = opcoes.limiar;

  if (!opcoes.dither) {
    for (let y = 0; y < altura; y++) {
      for (let x = 0; x < largura; x++) {
        if (cinza[y * largura + x] < limiar) escreverPixel(bmp, x, y, true);
      }
    }
    return bmp;
  }

  for (let y = 0; y < altura; y++) {
    for (let x = 0; x < largura; x++) {
      const i = y * largura + x;
      const antigo = cinza[i];
      const novo = antigo < limiar ? 0 : 255;
      const erro = antigo - novo;
      if (novo === 0) escreverPixel(bmp, x, y, true);
      if (x + 1 < largura) cinza[i + 1] += (erro * 7) / 16;
      if (y + 1 < altura) {
        if (x > 0) cinza[i + largura - 1] += (erro * 3) / 16;
        cinza[i + largura] += (erro * 5) / 16;
        if (x + 1 < largura) cinza[i + largura + 1] += (erro * 1) / 16;
      }
    }
  }
  return bmp;
};

/** Desenha o bitmap em um canvas HTML (preview WYSIWYG local). */
export const desenharBitmapNoCanvas = (
  bmp: BitmapMono,
  canvas: HTMLCanvasElement,
) => {
  canvas.width = bmp.largura;
  canvas.height = bmp.altura;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  const img = ctx.createImageData(bmp.largura, bmp.altura);
  for (let y = 0; y < bmp.altura; y++) {
    for (let x = 0; x < bmp.largura; x++) {
      const p = (y * bmp.largura + x) * 4;
      const preto = lerPixel(bmp, x, y);
      img.data[p] = img.data[p + 1] = img.data[p + 2] = preto ? 0 : 255;
      img.data[p + 3] = preto ? 255 : 0;
    }
  }
  ctx.putImageData(img, 0, 0);
};

/* ------------------------------------------------------------------ */
/* Geração de ZPL                                                      */
/* ------------------------------------------------------------------ */

export type ContextoEtiqueta = {
  larguraMm: number;
  alturaMm: number;
  dpmm: number;
};

/** Escapa ^ ~ _ usando ^FH (hexadecimal escape do ZPL). */
const codificarDados = (texto: string) => {
  if (!/[\^~_]/.test(texto)) return { precisaFh: false, valor: texto };
  const valor = texto.replace(
    /[\^~_]/g,
    (c) => `_${c.charCodeAt(0).toString(16).toUpperCase().padStart(2, "0")}`,
  );
  return { precisaFh: true, valor };
};

const zplTexto = (el: ElementoTexto, ctx: ContextoEtiqueta) => {
  const rot = ROTACAO_ZPL[el.rotacao];
  const h = Math.max(1, mmParaDots(el.alturaFonte, ctx.dpmm));
  const w = Math.max(1, mmParaDots(el.larguraFonte, ctx.dpmm));
  const { precisaFh, valor } = codificarDados(el.texto ?? "");

  let out = `^FO${mmParaDots(el.x, ctx.dpmm)},${mmParaDots(el.y, ctx.dpmm)}`;
  out += `^A${el.fonte}${rot},${h},${w}`;
  if (el.larguraBloco && el.larguraBloco > 0) {
    out += `^FB${mmParaDots(el.larguraBloco, ctx.dpmm)},${
      el.maxLinhas || 1
    },0,${el.alinhamento || "L"},0`;
  }
  if (el.inverso) out += "^FR";
  if (precisaFh) out += "^FH";
  out += `^FD${valor}^FS`;
  return out;
};

const COMANDO_BARRAS: Record<
  Simbologia,
  (rot: string, h: number, f: string, g: string, razao: number) => string
> = {
  CODE128: (rot, h, f, g) => `^BC${rot},${h},${f},${g},N`,
  CODE39: (rot, h, f, g) => `^B3${rot},N,${h},${f},${g}`,
  CODE93: (rot, h, f, g) => `^BA${rot},${h},${f},${g},N`,
  EAN13: (rot, h, f, g) => `^BE${rot},${h},${f},${g}`,
  EAN8: (rot, h, f, g) => `^B8${rot},${h},${f},${g}`,
  UPCA: (rot, h, f, g) => `^BU${rot},${h},${f},${g},N`,
  ITF: (rot, h, f, g) => `^B2${rot},${h},${f},${g}`,
};

const zplBarras = (el: ElementoBarras, ctx: ContextoEtiqueta) => {
  const rot = ROTACAO_ZPL[el.rotacao];
  const h = Math.max(1, mmParaDots(el.altura, ctx.dpmm));
  const f = el.mostrarTexto ? "Y" : "N";
  const g = el.mostrarTexto && el.textoAcima ? "Y" : "N";
  const { precisaFh, valor } = codificarDados(el.dados ?? "");

  let out = `^FO${mmParaDots(el.x, ctx.dpmm)},${mmParaDots(el.y, ctx.dpmm)}`;
  out += `^BY${Math.max(1, Math.round(el.modulo))},${el.razao || 3},${h}`;
  out += COMANDO_BARRAS[el.simbologia](rot, h, f, g, el.razao);
  if (precisaFh) out += "^FH";
  out += `^FD${valor}^FS`;
  return out;
};

const zplQrcode = (el: ElementoQrcode, ctx: ContextoEtiqueta) => {
  const rot = ROTACAO_ZPL[el.rotacao];
  const mag = Math.min(10, Math.max(1, Math.round(el.magnificacao)));
  const dados = (el.dados ?? "").replace(/\^/g, " ").replace(/~/g, " ");
  return (
    `^FO${mmParaDots(el.x, ctx.dpmm)},${mmParaDots(el.y, ctx.dpmm)}` +
    `^BQ${rot},2,${mag},${el.correcao}` +
    `^FD${el.correcao}A,${dados}^FS`
  );
};

const zplImagem = (el: ElementoImagem, ctx: ContextoEtiqueta) => {
  const bmp = rotacionarBitmap(bitmapDeElemento(el), el.rotacao);
  const total = bmp.bytesPorLinha * bmp.altura;
  return (
    `^FO${mmParaDots(el.x, ctx.dpmm)},${mmParaDots(el.y, ctx.dpmm)}` +
    `^GFA,${total},${total},${bmp.bytesPorLinha},${bitmapParaHex(bmp)}^FS`
  );
};

const zplCaixa = (el: ElementoCaixa, ctx: ContextoEtiqueta) => {
  const w = mmParaDots(el.largura, ctx.dpmm);
  const h = mmParaDots(el.altura, ctx.dpmm);
  const t = el.preenchido
    ? Math.max(w, h)
    : Math.max(1, Math.round(el.espessura));
  const cor = el.inverso ? "W" : "B";
  return (
    `^FO${mmParaDots(el.x, ctx.dpmm)},${mmParaDots(el.y, ctx.dpmm)}` +
    `^GB${w},${h},${t},${cor},${el.arredondamento || 0}^FS`
  );
};

export const zplDoElemento = (
  el: EtiquetaElemento,
  ctx: ContextoEtiqueta,
): string => {
  if (el.oculto) return "";
  switch (el.tipo) {
    case "texto":
      return zplTexto(el, ctx);
    case "barras":
      return zplBarras(el, ctx);
    case "qrcode":
      return zplQrcode(el, ctx);
    case "imagem":
      return zplImagem(el, ctx);
    case "caixa":
    case "linha":
      return zplCaixa(el, ctx);
    case "bruto":
      return el.zpl;
    default:
      return "";
  }
};

export const gerarZpl = (
  design: EtiquetaDesign,
  ctx: ContextoEtiqueta,
): string => {
  const linhas: string[] = [];
  // ~DG/~DY precisam rodar fora do formato, senão o ^XG não acha o arquivo
  if (design.downloads?.trim()) linhas.push(design.downloads.trim());
  linhas.push("^XA", design.codificacao || "^CI28");
  linhas.push(`^PW${mmParaDots(ctx.larguraMm, ctx.dpmm)}`);
  linhas.push(`^LL${mmParaDots(ctx.alturaMm, ctx.dpmm)}`);
  if (design.preambulo?.trim()) linhas.push(design.preambulo.trim());
  for (const el of design.elementos) {
    const zpl = zplDoElemento(el, ctx);
    if (zpl) linhas.push(zpl);
  }
  if (design.posfixo?.trim()) linhas.push(design.posfixo.trim());
  linhas.push("^XZ");
  return linhas.join("\n");
};

/* ------------------------------------------------------------------ */
/* Importação de ZPL (best-effort)                                     */
/* ------------------------------------------------------------------ */

type Token = { cmd: string; args: string; inicio: number; fim: number };

const tokenizar = (zpl: string): Token[] => {
  const tokens: Token[] = [];
  let i = 0;
  while (i < zpl.length) {
    const c = zpl[i];
    if (c !== "^" && c !== "~") {
      i++;
      continue;
    }
    const inicio = i;
    const cmd = zpl.slice(i + 1, i + 3).toUpperCase();
    let fim: number;
    if (cmd === "FD" || cmd === "FV") {
      // dados livres até o ^FS
      const fs = zpl.indexOf("^FS", i + 3);
      fim = fs === -1 ? zpl.length : fs;
    } else {
      let j = i + 3;
      while (j < zpl.length && zpl[j] !== "^" && zpl[j] !== "~") j++;
      fim = j;
    }
    tokens.push({ cmd, args: zpl.slice(i + 3, fim), inicio, fim });
    i = fim;
  }
  return tokens;
};

/* ------------------------------------------------------------------ */
/* Downloads gráficos (~DG / ~DY)                                      */
/* ------------------------------------------------------------------ */

/**
 * Comandos de download que precisam ser executados FORA de um formato.
 * `~DG`/`~DY` gravam o arquivo na memória da impressora; dentro de
 * `^XA...^XZ` a firmware ignora ou trunca, e o `^XG` seguinte não acha nada.
 */
const COMANDOS_DOWNLOAD = ["DG", "DY", "DB", "DU"];

export type ZplSeparado = {
  /** blocos ~DG/~DY, na ordem original, prontos pra enviar antes do formato */
  downloads: string;
  /** o restante do ZPL, já sem os downloads */
  corpo: string;
};

/**
 * Separa os downloads gráficos do corpo da etiqueta. Aceita ZPL onde o
 * `~DG` foi escrito por engano dentro do `^XA` — é o caso mais comum quando
 * o modelo veio do ZebraDesigner.
 */
export const separarDownloadsGraficos = (zpl: string): ZplSeparado => {
  const blocos: string[] = [];
  let corpo = "";
  let i = 0;

  while (i < zpl.length) {
    const c = zpl[i];
    if (c !== "~") {
      corpo += c;
      i++;
      continue;
    }
    const cmd = zpl.slice(i + 1, i + 3).toUpperCase();
    if (!COMANDOS_DOWNLOAD.includes(cmd)) {
      corpo += c;
      i++;
      continue;
    }
    // os dados vão até o próximo comando; o ^FS que costuma fechar faz parte
    let fim = i + 3;
    while (fim < zpl.length && zpl[fim] !== "^" && zpl[fim] !== "~") fim++;
    if (zpl.slice(fim, fim + 3).toUpperCase() === "^FS") fim += 3;
    blocos.push(zpl.slice(i, fim).trim());
    i = fim;
  }

  return {
    downloads: blocos.join("\n"),
    corpo: corpo.replace(/^[\s]*\n/gm, "").trim(),
  };
};

/**
 * Quebra um bloco de download gráfico (`~DG`/`~DY`) nos pacotes que vão para
 * o módulo, sempre cortando em cima de uma vírgula.
 *
 * O firmware acrescenta um `\n` no fim de todo pacote que repassa para a
 * impressora. Com o corte cego de 500 caracteres esse `\n` cai no meio de uma
 * linha do gráfico; a impressora interrompe o download ali, continua
 * esperando os bytes que faltam de `~DGd:o.x,t,w` e passa a engolir o
 * `^XA...^XZ` seguinte como se ainda fosse dado do gráfico — nada é impresso
 * e ela só volta a imprimir depois de reiniciada. A vírgula é o separador de
 * linha do formato ACS, então cortando nela a quebra cai entre linhas, onde o
 * `\n` é ignorado.
 *
 * O limite continua em 500: o buffer de saída USB do firmware é
 * `wMaxPacketSize * 8` = 512 bytes, e o pacote ainda ganha o `\n`.
 */
export const dividirDownloadGraficoEmPacotes = (
  bloco: string,
  limite = 500,
): string[] => {
  const partes: string[] = [];
  let i = 0;
  while (i < bloco.length) {
    let fim = Math.min(i + limite, bloco.length);
    if (fim < bloco.length) {
      const corte = bloco.lastIndexOf(",", fim - 1);
      // só aceita se o corte andar para frente; senão cai no corte cego
      if (corte >= i) fim = corte + 1;
    }
    partes.push(bloco.slice(i, fim));
    i = fim;
  }
  return partes;
};

/**
 * Devolve o ZPL com os downloads gráficos hasteados para antes do `^XA`,
 * que é o formato esperado pelo caminho de impressão (ele envia o "logo"
 * em pacotes separados, antes do formato da etiqueta).
 */
export const normalizarDownloadsGraficos = (zpl: string): string => {
  if (!zpl) return zpl;
  const { downloads, corpo } = separarDownloadsGraficos(zpl);
  if (!downloads) return zpl;
  return `${downloads}\n${corpo}`;
};

/** Descompacta o formato ACS (":" / letras de repetição) do ^GFA. */
const descompactarGfa = (
  dados: string,
  bytesPorLinha: number,
  totalBytes: number,
): string | null => {
  if (/^[0-9A-Fa-f\s]*$/.test(dados)) return dados.replace(/\s+/g, "");
  if (dados.includes("Z64") || dados.includes("B64")) return null;

  const larguraHex = bytesPorLinha * 2;
  let saida = "";
  let linhaAtual = "";
  let linhaAnterior = "";
  let repeticao = 0;

  // linha cheia fecha sozinha; o "," seguinte é só separador, não linha vazia
  let fechouSozinha = false;

  const fecharLinha = () => {
    if (linhaAtual.length < larguraHex)
      linhaAtual = linhaAtual.padEnd(larguraHex, "0");
    saida += linhaAtual;
    linhaAnterior = linhaAtual;
    linhaAtual = "";
  };

  for (const ch of dados) {
    if (/\s/.test(ch)) continue;
    if (ch === ",") {
      if (fechouSozinha) {
        fechouSozinha = false;
        continue;
      }
      fecharLinha();
      continue;
    }
    if (ch === "!") {
      if (fechouSozinha) {
        fechouSozinha = false;
        continue;
      }
      linhaAtual = linhaAtual.padEnd(larguraHex, "F");
      fecharLinha();
      continue;
    }
    if (ch === ":") {
      fechouSozinha = false;
      if (linhaAtual.length) fecharLinha();
      saida += linhaAnterior;
      continue;
    }
    if (ch >= "G" && ch <= "Y") {
      repeticao += ch.charCodeAt(0) - 70; // G = 1
      continue;
    }
    if (ch >= "g" && ch <= "z") {
      repeticao += (ch.charCodeAt(0) - 102) * 20; // g = 20
      continue;
    }
    if (/[0-9A-Fa-f]/.test(ch)) {
      const vezes = repeticao || 1;
      linhaAtual += ch.toUpperCase().repeat(vezes);
      repeticao = 0;
      fechouSozinha = false;
      if (linhaAtual.length >= larguraHex) {
        linhaAtual = linhaAtual.slice(0, larguraHex);
        fecharLinha();
        fechouSozinha = true;
      }
      continue;
    }
    return null; // caractere desconhecido: não arrisca
  }
  if (linhaAtual.length) fecharLinha();
  return saida.slice(0, totalBytes * 2).padEnd(totalBytes * 2, "0");
};

const SIMBOLOGIA_POR_CMD: Record<string, Simbologia> = {
  BC: "CODE128",
  B3: "CODE39",
  BA: "CODE93",
  BE: "EAN13",
  B8: "EAN8",
  BU: "UPCA",
  B2: "ITF",
};

export type ResultadoImportacao = {
  design: EtiquetaDesign;
  avisos: string[];
};

/**
 * Converte ZPL existente em elementos editáveis. O que não for reconhecido
 * vira elemento "bruto" — a importação nunca perde conteúdo.
 */
export const importarZpl = (
  zplEntrada: string,
  ctx: ContextoEtiqueta,
): ResultadoImportacao => {
  const avisos: string[] = [];
  const elementos: EtiquetaElemento[] = [];
  const preambulo: string[] = [];

  const inicioXaOriginal = zplEntrada.indexOf("^XA");
  const inicioDownloadOriginal = zplEntrada.search(/~D[GYBU]/i);
  const separado = separarDownloadsGraficos(zplEntrada);
  const downloads = separado.downloads || undefined;
  const zpl = downloads ? separado.corpo : zplEntrada;

  if (
    downloads &&
    inicioXaOriginal !== -1 &&
    inicioDownloadOriginal > inicioXaOriginal
  ) {
    avisos.push(
      "Comandos de download de imagem (~DG) estavam dentro do ^XA e foram movidos para antes do formato.",
    );
  }

  const inicioCorpo = zpl.indexOf("^XA");
  const fimCorpo = zpl.lastIndexOf("^XZ");
  if (inicioCorpo === -1) {
    return {
      design: {
        versao: 1,
        downloads,
        elementos: [
          {
            id: novoId(),
            tipo: "bruto",
            nome: "ZPL importado",
            x: 0,
            y: 0,
            rotacao: 0,
            zpl: zpl.trim(),
          } as ElementoBruto,
        ],
      },
      avisos: ["ZPL sem ^XA — mantido como bloco bruto."],
    };
  }
  if (inicioCorpo > 0 && zpl.slice(0, inicioCorpo).trim()) {
    preambulo.push(zpl.slice(0, inicioCorpo).trim());
    avisos.push(
      "Comandos antes do ^XA (ex.: download de logo ~DG) foram preservados no preâmbulo.",
    );
  }

  const corpo = zpl.slice(
    inicioCorpo + 3,
    fimCorpo === -1 ? zpl.length : fimCorpo,
  );
  const tokens = tokenizar(corpo);

  // acumulador do campo atual
  let campo: Token[] = [];
  let temPosicao = false;
  // ^BY declarado fora de campo vale para os códigos de barras seguintes
  let byPadrao: string | null = null;
  let codificacao: string | undefined;
  // trechos contíguos fora de campo (ex.: ^RFW...^FD...^FS) preservados na íntegra
  let faixaLivre: [number, number] | null = null;

  const fecharFaixaLivre = () => {
    if (!faixaLivre) return;
    const texto = corpo.slice(faixaLivre[0], faixaLivre[1]).trim();
    if (texto) preambulo.push(texto);
    faixaLivre = null;
  };

  const finalizarCampo = (fimIndice: number) => {
    if (!campo.length) return;
    const bruto = corpo.slice(campo[0].inicio, fimIndice).trim();
    const el = interpretarCampo(campo, ctx, bruto, avisos, byPadrao);
    if (el) elementos.push(el);
    campo = [];
    temPosicao = false;
  };

  for (const tk of tokens) {
    if (tk.cmd === "FO" || tk.cmd === "FT") {
      fecharFaixaLivre();
      if (temPosicao) finalizarCampo(tk.inicio);
      campo = [tk];
      temPosicao = true;
      continue;
    }
    if (!temPosicao) {
      // comandos de nível de etiqueta que o gerador reemite sozinho
      if (["PW", "LL", "XA", "XZ", "LH"].includes(tk.cmd)) {
        fecharFaixaLivre();
        continue;
      }
      if (tk.cmd === "CI") {
        fecharFaixaLivre();
        codificacao = corpo.slice(tk.inicio, tk.fim).trim();
        continue;
      }
      if (tk.cmd === "BY") byPadrao = tk.args;
      // mantém o trecho original inteiro — quebrar ^FD/^FS corromperia os dados
      if (faixaLivre) faixaLivre[1] = tk.fim;
      else faixaLivre = [tk.inicio, tk.fim];
      continue;
    }
    campo.push(tk);
    if (tk.cmd === "FS") finalizarCampo(tk.fim);
  }
  fecharFaixaLivre();
  finalizarCampo(corpo.length);

  return {
    design: {
      versao: 1,
      elementos,
      downloads,
      codificacao,
      preambulo: preambulo.join("\n") || undefined,
    },
    avisos,
  };
};

const interpretarCampo = (
  campo: Token[],
  ctx: ContextoEtiqueta,
  bruto: string,
  avisos: string[],
  byPadrao: string | null,
): EtiquetaElemento | null => {
  const posicao = campo[0];
  const [xs, ys] = posicao.args.split(",");
  // ^FT ancora na linha de base (parte de baixo); o editor trabalha com o topo
  const ehBaseline = posicao.cmd === "FT";
  const base = {
    id: novoId(),
    x: dotsParaMm(parseInt(xs || "0", 10) || 0, ctx.dpmm),
    y: dotsParaMm(parseInt(ys || "0", 10) || 0, ctx.dpmm),
    rotacao: 0 as Rotacao,
  };
  const ajustarBaseline = <T extends EtiquetaElemento>(el: T): T => {
    if (!ehBaseline) return el;
    // no ^FT a linha interpretativa do código de barras fica ABAIXO da âncora
    const altura =
      el.tipo === "barras"
        ? el.altura
        : dimensoesElemento(el, ctx).altura;
    return { ...el, y: Math.round((el.y - altura) * 100) / 100 };
  };

  const brutoFallback = (motivo?: string): ElementoBruto => {
    if (motivo) avisos.push(motivo);
    return { ...base, tipo: "bruto", nome: "ZPL não interpretado", zpl: bruto };
  };

  const achar = (...cmds: string[]) =>
    campo.find((t) => cmds.includes(t.cmd)) || null;

  const tokenDados = achar("FD", "FV");
  const tokenGb = achar("GB");
  const tokenGf = achar("GF");
  const tokenFonte = campo.find((t) => t.cmd.startsWith("A") && t.cmd !== "AT");
  const tokenBarras = campo.find((t) => SIMBOLOGIA_POR_CMD[t.cmd]);
  const tokenQr = achar("BQ");
  const tokenBy = achar("BY");
  const tokenFb = achar("FB");
  const temFr = !!achar("FR");
  const tokenFh = achar("FH");
  const temFh = !!tokenFh;
  const escapeFh = (tokenFh?.args || "_").trim().charAt(0) || "_";

  const conhecidos = new Set([
    "FO",
    "FT",
    "FS",
    "FD",
    "FV",
    "FR",
    "FH",
    "FB",
    "BY",
    "GB",
    "GF",
    "BQ",
    ...Object.keys(SIMBOLOGIA_POR_CMD),
  ]);
  const desconhecidos = campo.filter(
    (t) => !conhecidos.has(t.cmd) && t !== tokenFonte,
  );

  const decodificar = (valor: string) => {
    if (!temFh) return valor;
    let saida = "";
    for (let i = 0; i < valor.length; i++) {
      const par = valor.substr(i + 1, 2);
      if (valor[i] === escapeFh && /^[0-9A-Fa-f]{2}$/.test(par)) {
        saida += String.fromCharCode(parseInt(par, 16));
        i += 2;
      } else {
        saida += valor[i];
      }
    }
    return saida;
  };

  // ---- imagem ----
  if (tokenGf) {
    const args = tokenGf.args;
    const partes = args.split(",");
    if (partes.length < 5 || partes[0].toUpperCase() !== "A")
      return brutoFallback("Imagem em formato ^GF não-ASCII mantida como bruta.");
    const totalBytes = parseInt(partes[2] || partes[1], 10) || 0;
    const bytesPorLinha = parseInt(partes[3], 10) || 1;
    const dados = partes.slice(4).join(",");
    const hex = descompactarGfa(dados, bytesPorLinha, totalBytes);
    if (!hex)
      return brutoFallback(
        "Imagem com compressão não suportada (Z64/B64) mantida como bloco bruto.",
      );
    const alturaDots = Math.max(1, Math.round(totalBytes / bytesPorLinha));
    const el: ElementoImagem = {
      ...base,
      tipo: "imagem",
      nome: "Imagem importada",
      larguraDots: bytesPorLinha * 8,
      alturaDots,
      bytesPorLinha,
      hex: hex.toUpperCase(),
    };
    return ajustarBaseline(el);
  }

  // ---- caixa / linha ----
  if (tokenGb) {
    const [w, h, t, cor, round] = tokenGb.args.split(",");
    const largura = dotsParaMm(parseInt(w || "0", 10) || 0, ctx.dpmm);
    const altura = dotsParaMm(parseInt(h || "0", 10) || 0, ctx.dpmm);
    const espessura = parseInt(t || "1", 10) || 1;
    const wDots = parseInt(w || "0", 10) || 0;
    const hDots = parseInt(h || "0", 10) || 0;
    const menorLado = Math.min(wDots, hDots);
    // ^GB com espessura >= um dos lados desenha barra sólida, não moldura
    const solido = menorLado > 0 && espessura >= menorLado;
    const ehLinha = solido && menorLado <= 3 * espessura && menorLado < 24;
    const el: ElementoCaixa = {
      ...base,
      tipo: ehLinha || menorLado === 0 ? "linha" : "caixa",
      largura,
      altura,
      espessura,
      preenchido: solido && !ehLinha ? true : undefined,
      arredondamento: parseInt(round || "0", 10) || 0,
      inverso: (cor || "B").toUpperCase() === "W",
    };
    return ajustarBaseline(el);
  }

  // ---- QR ----
  if (tokenQr) {
    const [rot, , mag, ec] = tokenQr.args.split(",");
    let dados = decodificar(tokenDados?.args ?? "");
    const virgula = dados.indexOf(",");
    // formato "<ec><input>,<dados>"
    if (virgula > 0 && virgula <= 3) dados = dados.slice(virgula + 1);
    const el: ElementoQrcode = {
      ...base,
      rotacao: ZPL_ROTACAO[(rot || "N").toUpperCase()] ?? 0,
      tipo: "qrcode",
      dados,
      magnificacao: parseInt(mag || "3", 10) || 3,
      correcao: (["L", "M", "Q", "H"].includes((ec || "M").toUpperCase())
        ? (ec || "M").toUpperCase()
        : "M") as ElementoQrcode["correcao"],
    };
    return ajustarBaseline(el);
  }

  // ---- código de barras ----
  if (tokenBarras) {
    const simbologia = SIMBOLOGIA_POR_CMD[tokenBarras.cmd];
    const args = tokenBarras.args.split(",");
    const rot = args[0] || "N";
    // ^B3 tem "e" antes da altura
    const idxAltura = tokenBarras.cmd === "B3" ? 2 : 1;
    const argsBy = (tokenBy?.args ?? byPadrao ?? "").split(",");
    const alturaDots =
      parseInt(args[idxAltura] || "", 10) ||
      parseInt(argsBy[2] || "", 10) ||
      mmParaDots(10, ctx.dpmm);
    const idxTexto = idxAltura + 1;
    const el: ElementoBarras = {
      ...base,
      rotacao: ZPL_ROTACAO[rot.toUpperCase()] ?? 0,
      tipo: "barras",
      simbologia,
      dados: decodificar(tokenDados?.args ?? ""),
      altura: dotsParaMm(alturaDots, ctx.dpmm),
      modulo: parseInt(argsBy[0] || "2", 10) || 2,
      razao: parseFloat(argsBy[1] || "3") || 3,
      mostrarTexto: (args[idxTexto] || "Y").toUpperCase() !== "N",
      textoAcima: (args[idxTexto + 1] || "N").toUpperCase() === "Y",
    };
    return ajustarBaseline(el);
  }

  // ---- texto ----
  if (tokenDados) {
    if (desconhecidos.length) return brutoFallback();
    const fonteCmd = tokenFonte?.cmd ?? "A0";
    const letra = fonteCmd[1];
    const args = (tokenFonte?.args ?? "").split(",");
    // ^A0N,30,30 → cmd = "A0", args = "N,30,30"
    const rotLetra = (args[0] || "N").toUpperCase();
    const alturaDots = parseInt(args[1] || "", 10) || mmParaDots(3, ctx.dpmm);
    const larguraDots = parseInt(args[2] || "", 10) || alturaDots;
    const fbArgs = tokenFb?.args.split(",") ?? [];
    const el: ElementoTexto = {
      ...base,
      rotacao: ZPL_ROTACAO[rotLetra] ?? 0,
      tipo: "texto",
      texto: decodificar(tokenDados.args),
      fonte: (["0", "A", "B", "D", "E", "F", "G", "H"].includes(letra)
        ? letra
        : "0") as FonteZpl,
      alturaFonte: dotsParaMm(alturaDots, ctx.dpmm),
      larguraFonte: dotsParaMm(larguraDots, ctx.dpmm),
      inverso: temFr || undefined,
      larguraBloco: fbArgs.length
        ? dotsParaMm(parseInt(fbArgs[0] || "0", 10) || 0, ctx.dpmm)
        : undefined,
      maxLinhas: fbArgs.length ? parseInt(fbArgs[1] || "1", 10) || 1 : undefined,
      alinhamento: fbArgs.length
        ? ((fbArgs[3] || "L").toUpperCase() as ElementoTexto["alinhamento"])
        : undefined,
    };
    return ajustarBaseline(el);
  }

  if (!bruto.trim()) return null;
  return brutoFallback();
};

/* ------------------------------------------------------------------ */
/* Pré-visualização (Labelary)                                         */
/* ------------------------------------------------------------------ */

const polegadas = (mm: number) => (Number(mm) / 25.4).toFixed(2);

export type ResultadoPreview =
  | { ok: true; url: string }
  | { ok: false; erro: string };

/**
 * Renderiza via POST (o GET estoura o limite de URL assim que entra um logo).
 */
export const renderizarPreview = async (
  zpl: string,
  ctx: ContextoEtiqueta,
  sinal?: AbortSignal,
): Promise<ResultadoPreview> => {
  zpl = normalizarDownloadsGraficos(zpl);
  const endpoint = `https://api.labelary.com/v1/printers/${ctx.dpmm}dpmm/labels/${polegadas(
    ctx.larguraMm,
  )}x${polegadas(ctx.alturaMm)}/0/`;
  try {
    const resposta = await fetch(endpoint, {
      method: "POST",
      // a Labelary rejeita text/plain com 415; form-urlencoded passa
      // e continua sem disparar preflight de CORS
      headers: {
        Accept: "image/png",
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: zpl,
      signal: sinal,
    });
    if (!resposta.ok) {
      const texto = await resposta.text().catch(() => "");
      return {
        ok: false,
        erro: texto?.trim() || `Falha ao renderizar (HTTP ${resposta.status}).`,
      };
    }
    const blob = await resposta.blob();
    return { ok: true, url: URL.createObjectURL(blob) };
  } catch (e) {
    if ((e as Error)?.name === "AbortError") return { ok: false, erro: "" };
    return { ok: false, erro: "Não foi possível contatar o renderizador." };
  }
};

/* ------------------------------------------------------------------ */
/* Fábricas de elementos                                               */
/* ------------------------------------------------------------------ */

export const criarElemento = (
  tipo: Exclude<EtiquetaElemento["tipo"], "imagem">,
  ctx: ContextoEtiqueta,
  posicao?: { x: number; y: number },
): EtiquetaElemento => {
  const x = posicao?.x ?? Math.round(ctx.larguraMm * 0.1 * 10) / 10;
  const y = posicao?.y ?? Math.round(ctx.alturaMm * 0.1 * 10) / 10;
  const base = { id: novoId(), x, y, rotacao: 0 as Rotacao };

  switch (tipo) {
    case "texto":
      return {
        ...base,
        tipo: "texto",
        texto: "Texto",
        fonte: "0",
        alturaFonte: 4,
        larguraFonte: 4,
      };
    case "barras":
      return {
        ...base,
        tipo: "barras",
        dados: "1234567890",
        simbologia: "CODE128",
        altura: 10,
        modulo: 2,
        razao: 3,
        mostrarTexto: true,
        textoAcima: false,
      };
    case "qrcode":
      return {
        ...base,
        tipo: "qrcode",
        dados: "https://inovacode.app.br",
        magnificacao: 4,
        correcao: "M",
      };
    case "caixa":
      return {
        ...base,
        tipo: "caixa",
        largura: Math.min(30, ctx.larguraMm * 0.5),
        altura: Math.min(15, ctx.alturaMm * 0.4),
        espessura: 2,
        arredondamento: 0,
      };
    case "linha":
      return {
        ...base,
        tipo: "linha",
        largura: Math.min(30, ctx.larguraMm * 0.6),
        altura: 0,
        espessura: 2,
        arredondamento: 0,
      };
    case "bruto":
    default:
      return { ...base, tipo: "bruto", nome: "Bloco ZPL", zpl: "^FS" };
  }
};

/** Bounding box aproximada em mm — usada pelo canvas e pelo snap. */
export const dimensoesElemento = (
  el: EtiquetaElemento,
  ctx: ContextoEtiqueta,
): { largura: number; altura: number } => {
  let largura = 0;
  let altura = 0;
  switch (el.tipo) {
    case "texto": {
      const linhas = (el.texto || " ").split("\n");
      const maiorLinha = Math.max(...linhas.map((l) => l.length), 1);
      largura =
        el.larguraBloco && el.larguraBloco > 0
          ? el.larguraBloco
          : maiorLinha * el.larguraFonte * 0.62;
      altura = el.alturaFonte * Math.max(linhas.length, 1);
      break;
    }
    case "barras": {
      const digitos = Math.max((el.dados || "").length, 1);
      largura = dotsParaMm(digitos * el.modulo * 11, ctx.dpmm);
      altura = el.altura + (el.mostrarTexto ? 3 : 0);
      break;
    }
    case "qrcode": {
      const lado = dotsParaMm(el.magnificacao * 25, ctx.dpmm);
      largura = lado;
      altura = lado;
      break;
    }
    case "imagem": {
      largura = dotsParaMm(el.larguraDots, ctx.dpmm);
      altura = dotsParaMm(el.alturaDots, ctx.dpmm);
      if (el.rotacao === 90 || el.rotacao === 270) {
        const t = largura;
        largura = altura;
        altura = t;
      }
      break;
    }
    case "caixa":
    case "linha": {
      largura = Math.max(el.largura, dotsParaMm(el.espessura, ctx.dpmm));
      altura = Math.max(el.altura, dotsParaMm(el.espessura, ctx.dpmm));
      break;
    }
    case "bruto":
      largura = 12;
      altura = 6;
      break;
  }
  if (el.tipo !== "imagem" && (el.rotacao === 90 || el.rotacao === 270)) {
    const t = largura;
    largura = altura;
    altura = t;
  }
  return { largura: Math.max(largura, 0.5), altura: Math.max(altura, 0.5) };
};

export const rotuloElemento = (el: EtiquetaElemento): string => {
  if (el.nome?.trim()) return el.nome.trim();
  switch (el.tipo) {
    case "texto":
      return el.texto?.slice(0, 24) || "Texto";
    case "barras":
      return `${el.simbologia} ${el.dados?.slice(0, 14) || ""}`.trim();
    case "qrcode":
      return `QR ${el.dados?.slice(0, 18) || ""}`.trim();
    case "imagem":
      return el.arquivo || "Imagem";
    case "caixa":
      return "Caixa";
    case "linha":
      return "Linha";
    case "bruto":
      return "ZPL bruto";
    default:
      return "Elemento";
  }
};
